import React, { useState, useEffect } from 'react';
/* COMPONENTS */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Grid, IconButton, Autocomplete, autocompleteClasses, InputAdornment } from '@mui/material';
/* TRANSLATE */
import { useTranslation } from 'react-i18next';
/* ICONS */
import CloseIcon from '@mui/icons-material/Close';
import { Visibility } from '@mui/icons-material';
import { VisibilityOff } from '@mui/icons-material';
/* REQUEST */
import { addUser, getProfiles } from 'utils/Users';
/* HASH */
import { handleHash, validatePassword } from 'utils/Login';

const AddUser = ({ open, setOpen, setAlert, setOpenAlert, reloading }) => {
    const { t } = useTranslation();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(false);
    const [helperText, setHelperText] = useState('');
    const [loading, setLoading] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [email, setEmail] = useState('');
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const response = async () => {
            try {
                const profiles = await getProfiles(setLoading);
                console.log('Profiles:', profiles);
                setProfiles(profiles);
            } catch (error) {
                console.error('Error fetching profiles:', error.message);
            }
        };

        if (open) response();
    }, [open]);

    const cleanForm = () => {
        setOpen(false);
        setUsername('');
        setPassword('');
        setEmail('');
        setSelectedProfile(null);
        setError(false);
        setHelperText('');
    };

    return (
        <>
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth={'xs'} size={'small'} aria-labelledby="user-dialog" aria-describedby="user-dialog-description">
                <DialogTitle>{t('user_management.table.dialog.add.title')}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => cleanForm()}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                sx={{ mt: 1 }}
                                id={'input-username'}
                                fullWidth
                                label={t('user_management.table.dialog.add.username')}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                autoComplete="off"
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                sx={{ mt: 1 }}
                                id={'input-password'}
                                onBlur={(e) => (e.target.value !== '' ? validatePassword(e.target.value, setError, setHelperText) : null)}
                                fullWidth
                                error={error}
                                helperText={helperText}
                                type={showPassword ? 'text' : 'password'}
                                label={t('user_management.table.dialog.add.password')}
                                value={password}
                                onChange={(e) => {
                                    if (e.target.value === '') {
                                        setPassword(e.target.value);
                                        setError(false);
                                        setHelperText('');
                                    } else {
                                        const newValue = e.target.value;
                                        setPassword(newValue);

                                        // Puedes validar mientras escribe o al terminar de escribir usando onBlur
                                        validatePassword(newValue, setError, setHelperText);
                                    }
                                }}
                                required
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ backgroundColor: 'transparent' }}>
                                                <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} edge="end">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <TextField
                                sx={{ mt: 1 }}
                                id={'input-email'}
                                fullWidth
                                label={t('user_management.table.dialog.add.email')}
                                value={email}
                                required
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="off"
                            ></TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                            <Autocomplete
                                disabled={loading}
                                options={profiles ? profiles.sort((a, b) => -b.name.localeCompare(a.name)) : []}
                                getOptionLabel={(option) => option.name}
                                onChange={(e, value) => setSelectedProfile(value)}
                                sx={{ width: '100%', mt: 1 }}
                                renderInput={(params) => <TextField {...params} required label={t('user_management.table.dialog.add.select.select_profile')} />}
                                renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <Box
                                            key={key}
                                            sx={{
                                                borderRadius: '5px',
                                                margin: '5px',
                                                [`&.${autocompleteClasses.option}`]: {
                                                    padding: '8px',
                                                },
                                            }}
                                            component="li"
                                            {...optionProps}
                                        >
                                            {option.name}
                                        </Box>
                                    );
                                }}
                                slotProps={{
                                    listbox: {
                                        sx: {
                                            height: 200,
                                            overflowY: 'auto',
                                        },
                                    },
                                    popper: {
                                        placement: 'bottom-start',
                                        modifiers: [{ name: 'flip', enabled: false }],
                                    },
                                    textField: () => ({
                                        required: true,
                                    }),
                                }}
                                required
                                noOptionsText={t('user_management.table.dialog.add.select.no_options')}
                                loadingText={t('user_management.table.dialog.add.select.loading')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="error" onClick={() => cleanForm()}>
                        {t('user_management.table.dialog.add.buttons.cancel')}
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        disabled={username === '' || password === '' || selectedProfile === null || email === ''}
                        onClick={async () => {
                            try {
                                const hash = await handleHash(password);

                                addUser(setLoading, {
                                    username: username,
                                    password: hash,
                                    profile_id: selectedProfile?.profile_id,
                                    email: email,
                                });
                                setOpenAlert(true);
                                setAlert({
                                    msg: t('user_management.table.dialog.add.messages.success'),
                                    severity: 'success',
                                    time: 6000,
                                });
                                reloading();
                                cleanForm();
                            } catch (e) {
                                setOpenAlert(true);
                                setAlert({
                                    msg: t('user_management.table.dialog.add.messages.error'),
                                    severity: 'error',
                                    time: 6000,
                                });
                                cleanForm();
                                console.error(e);
                            }
                        }}
                    >
                        {t('user_management.table.dialog.add.buttons.add')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { AddUser };
